import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { plainToInstance } from 'class-transformer';
import { BehaviorSubject, Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { PusherService } from '../../services/pusher.service';
import { CompanyGroup } from '../models/company-group.model';

@Injectable({
  providedIn: 'root'
})
export class CompanyGroupsService {

  private baseUrl: string = '/:apiBase/companies/:companyId/company-groups';
  private groupByIdUrl: string = this.baseUrl + '/:groupId';

  private _collectionSubjects: { [companyId: number]: BehaviorSubject<CompanyGroup[]> } = {};

  constructor(
    private http: HttpClient,
    private pusherService: PusherService
  ) { }

  /**
   * Watches for changes in the [[CompanyGroup|Company groups]] of a given
   * company ID.
   */
  public watch(companyId: number): Observable<CompanyGroup[]> {
    return this.pusherService.subjectManager(
      {
        collection: this._collectionSubjects,
        key: companyId,
        getData: () => this.get(companyId)
      },
      {
        channel: `company_${companyId}`,
        event: 'company_groups'
      },
      []);
  }

  /** Returns all [[Company|Company's]] [[CompanyGroup|Company groups]], without members. */
  private get(companyId: number): Observable<CompanyGroup[]> {
    const url = this.baseUrl
      .replace(":companyId", companyId.toString());

    return this.http.get<CompanyGroup[]>(url).pipe(map(groups => {
      return plainToInstance(CompanyGroup, groups);
    }));
  }

  /** Returns a [[CompanyGroup|Company group]] with its members. */
  public getOne(companyId: number, groupId: number): Observable<CompanyGroup> {
    const url = this.groupByIdUrl
      .replace(":companyId", companyId.toString())
      .replace(":groupId", groupId.toString());

    return this.http.get<CompanyGroup>(url).pipe(map(group => {
      return plainToInstance(CompanyGroup, group);
    }));
  }

  /** Creates a new [[CompanyGroup|Company group]]. */
  public create(companyId: number, group: CompanyGroup): Observable<CompanyGroup> {
    const url = this.baseUrl
      .replace(":companyId", companyId.toString());

    return this.http.post<CompanyGroup>(url, this.payload(group)).pipe(map(group => {
      return plainToInstance(CompanyGroup, group);
    }));
  }

  /** Updates a [[CompanyGroup|Company group]] and replaces its members. */
  public update(companyId: number, group: CompanyGroup): Observable<CompanyGroup> {
    const url = this.groupByIdUrl
      .replace(":companyId", companyId.toString())
      .replace(":groupId", group.id.toString());

    return this.http.put<CompanyGroup>(url, this.payload(group)).pipe(map(group => {
      return plainToInstance(CompanyGroup, group);
    }));
  }

  /** Deletes a [[CompanyGroup|Company group]]. */
  public delete(companyId: number, group: CompanyGroup): Observable<any> {
    const url = this.groupByIdUrl
      .replace(":companyId", companyId.toString())
      .replace(":groupId", group.id.toString());

    return this.http.delete<any>(url);
  }

  /** The API expects only the members IDs. */
  private payload(group: CompanyGroup): any {
    return {
      name: group.name,
      description: group.description || null,
      members: (group.members || []).map(company => company.id)
    };
  }
}
